<template>
  <section>
    <div class="container">
      <div class="row">
        <div class="col-1">
          <v-icon
            icon="chevron-left"
            class="cursor-pointer"
            @click="() => $router.back()"
          />
        </div>
        <div class="col-10 text-center mb-4">
          <h1> Détail de mon sinistre </h1>
          <div> {{ productName }} </div>
        </div>
        <div class="col-12 col-md-10 mx-auto">
          <div class="row position-relative">
            <span
              v-for="i in 5"
              :key="i"
              class="pills-image"
              :class="[ `pill-card-1-${i}` ]"
            />
            <div class="col-12 col-md-4 mb-2">
              <div class="card shadow">
                <div class="card-body text-center p-4">
                  <div class="d-flex flex-column">
                    <v-icon
                      icon="contract"
                      width="20"
                      height="20"
                      class="mb-1"
                    />
                    <div class="mb-1">
                      Contrat lié
                    </div>
                    <router-link
                      v-slot="{ navigate }"
                      :to="`/client/contracts/${contractRouteId}`"
                      custom
                    >
                      <div
                        class="fw-bold cursor-pointer"
                        @click="navigate"
                      >
                        N° {{ getReference }}
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <div class="card shadow">
                <div class="card-body text-center p-4">
                  <div class="d-flex flex-column">
                    <v-icon
                      icon="calendar"
                      width="20"
                      height="20"
                      class="mb-1"
                    />
                    <div class="mb-1">
                      Date de la déclaration
                    </div>
                    <div class="fw-bold">
                      {{ getCreationDate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-4 mb-2">
              <div class="card shadow">
                <div class="card-body text-center p-4">
                  <div class="d-flex flex-column">
                    <v-icon
                      icon="traffic-cone"
                      width="20"
                      height="20"
                      class="mb-1"
                    />
                    <div class="mb-1">
                      Statut
                    </div>
                    <div class="fw-bold">
                      {{ getStatus.text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="claim.status === 'WAITING_FOR_PROOF' || 11 === 11"
      class="container mt-4"
    >
      <div class="row">
        <div class="col-12 col-md-10 mx-auto">
          <h4 class="text-blue-600">
            Ajouter un document
          </h4>
          <div class="card">
            <div class="card-body">
              <div class="d-flex flex-row">
                <div class="d-flex my-auto">
                  Veuillez ajouter tout document pouvant être utile
                </div>
                <v-loader-upload
                  :index="0"
                  title="Choisir un fichier"
                  icon-color="white"
                  class="my-auto ms-auto"
                  @upload="(value) => uploaded = value"
                />
              </div>
            </div>
          </div>
        </div>
        <transition name="slide">
          <div
            v-if="uploaded"
            class="col-12 col-md-10 mx-auto d-flex"
          >
            <v-loader
              title="Envoyer"
              icon="email-white"
              class="mt-2 ms-auto"
              :next="sendDocuments"
            />
          </div>
        </transition>
      </div>
    </div>
    <div
      v-show="documents.length > 0"
      class="container mt-4"
    >
      <div class="row">
        <div class="col-12 col-md-10 mx-auto">
          <h4 class="text-blue-600">
            Pièces fournies
          </h4>
          <div class="d-flex-flex-column mt-3">
            <div
              v-for="document in documents"
              :key="document.id"
              class="card shadow mb-2"
            >
              <div class="card-body fw-bold">
                {{ getFilename(document.path) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/utils/_Date';
import { getContractRouteId } from '@/utils/contractRoute';

import LoaderUpload from '@/components/common/helpers/LoaderUpload.vue';

export default {
  name: 'ClaimDetails',
  components: {
    'v-loader-upload': LoaderUpload,
  },
  data() {
    return {
      documents: [],
      uploaded: false,
    };
  },
  computed: {
    ...mapGetters({
      claims: 'claim/claims',
      products: 'rater/products',
    }),
    claim() {
      return this.claims
        .find(({ id }) => this.$route.params.claimId === id)
        || {};
    },
    contract() {
      return this.claim.subscription || this.claim.policy || {};
    },
    productName() {
      return (this.products
        .find(({ id }) => id === (this.contract.productId || this.claim.productId))
        || {})
        .name;
    },
    getReference() {
      return this.contract.reference;
    },
    contractRouteId() {
      return getContractRouteId(this.contract);
    },
    getCreationDate() {
      return formatDate(this.claim.createdAt);
    },
    getStatus() {
      const status = {
        CREATED: {
          text: 'Créé', color: 'blue-400', textColor: 'white',
        },
        IN_PROGRESS: {
          text: 'En cours', color: 'blue-400', textColor: 'white',
        },
        CLOSED: {
          text: 'Traité', color: 'green-300', textColor: 'blue-700',
        },
        DISMISSED: {
          text: 'Sans suite', color: 'gray-500', textColor: 'white',
        },
        WAITING_FOR_PROOF: {
          text: 'En attente de pièces', color: 'yellow-600', textColor: 'blue-700',
        },
        UNDER_REVIEW: {
          text: 'A color\'étude', color: 'blue-400', textColor: 'white',
        },
        CHECKING: {
          text: 'Vérification', color: 'blue-500', textColor: 'white',
        },
        HANDLED: {
          text: 'Pris en charge', color: 'blue-500', textColor: 'white',
        },
        DECLINED: {
          text: 'Refusé', color: 'red-700', textColor: 'white',
        },
      };
      return status[this.claim.status] || {};
    },
  },
  mounted() {
    this.$store.dispatch('claim/getClaimDocuments', this.$route.params.claimId)
      .then((documents) => {
        this.documents = documents;
      });
  },
  beforeUnmount() {
    this.$store.dispatch('claim/setValue', { attachments: [] });
  },
  methods: {
    getFilename(path) {
      return [...path.split('/')].pop();
    },
    async sendDocuments() {
      await this.$store.dispatch('claim/addDocumentsToClaim', this.claim);
      const documents = await this.$store.dispatch('claim/getClaimDocuments', this.$route.params.claimId);
      this.$children[5].deleteFiles();
      this.documents = documents;
      this.uploaded = false;
    },
  },
};
</script>
<style scoped>
.pills-image {
  height: 12px;
  border-radius: 60px;
  position: absolute;
  z-index: 0;
}

.pill-card-1-1 {
  top: -6px;
  left: 10%;
  width: 64px;
  background-color: var(--bs-blue-500);
}
.pill-card-1-2 {
  left: -24px;
  width: 64px;
  bottom: 50%;
  background-color: var(--bs-green-500);
}
.pill-card-1-3 {
  left: 40%;
  width: 64px;
  bottom: -24px;
  background-color: var(--bs-yellow-500);
}
.pill-card-1-4 {
  right: -16px;
  width: 64px;
  bottom: 0;
  background-color: var(--bs-pink-500);
}
.pill-card-1-5 {
  right: 24px;
  width: 64px;
  top: -16px;
  background-color: var(--bs-green-500);
}

@media screen and (min-width: 576px) {
  .pill-card-1-1 { top: -15%; }
}
</style>
